/**
 * TabularPreviewTable — the one grid for Parquet-backed tables, shared by the lake
 * (file preview, structural extraction scope) and structured ingest (verbatim rows,
 * marking rows for promotion).
 *
 * Rows come with a trailing `__row_index` column carrying the Parquet row position; the
 * grid keys, selects and reports rows by that value, never by display position.
 *
 * Selection modes:
 * - `select-header`  — a row click designates the header row (lake scope)
 * - `build-batch`    — row + column toggles and an Excel-like drag rectangle (lake scope)
 * - `select-rows`    — a row click toggles the whole row, drag selects a row range
 *                      (structured ingest: rows to promote; every column goes along)
 * - `null`           — read-only
 *
 * `emphasis` marks rows / cells a search led to (a hit in a row window); `scrollToRow`
 * brings a Parquet row into view once.
 */

import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import type { TabularColumn } from '@/services/api/lake'
import { cn } from '@/utils/cn'

export type TabularCell = string | number | boolean | null
export type TabularSelectionMode = 'select-header' | 'build-batch' | 'select-rows' | null

export const ROW_INDEX_COLUMN = '__row_index'

interface DragPreview {
  rowRange: [number, number] // [minRowPos, maxRowPos] — visual positions in rows[]
  colRange: [number, number] // [minColIdx, maxColIdx] — 0-based data col indices
}

export interface TabularEmphasis {
  /** Parquet row indices to mark (a search hit's rows). */
  rows: Set<number>
  /** Whether one cell carries the hit — rendered stronger than its row. */
  cell?: (rowIndex: number, columnName: string, value: TabularCell) => boolean
}

export interface TabularPreviewTableProps {
  columns: TabularColumn[] // from TabularQueryResponse (includes __row_index last)
  rows: TabularCell[][]
  // Pagination
  currentOffset: number // offset of the first row currently shown
  totalRows: number // total matching rows (filtered)
  onPrevPage: () => void
  onNextPage: () => void
  isNavigating: boolean // true while fetching prev/next page
  // Selection
  headerRowIndex: number | null // __row_index value of designated header row
  pendingRows: Set<number> // __row_index values of currently selected data rows
  pendingColumns: Set<number> // 0-based col indices of currently selected data columns
  selectionMode: TabularSelectionMode
  onRowClick: (rowIndex: number) => void // passes __row_index value
  onColumnClick: (colIndex: number) => void // passes 0-based col index (not __row_index col)
  /**
   * Called when the user completes a drag over data cells. Receives the __row_index
   * values and 0-based col indices covered by the rectangle (all columns in
   * `select-rows` mode). Replaces the current selection.
   */
  onDragSelect?: (rows: number[], cols: number[]) => void
  /** Override the outer container's className. Defaults to 'flex-1 flex flex-col overflow-hidden'. */
  className?: string
  /** What to print in the row-number gutter; defaults to the Parquet row index. */
  rowLabel?: (rowIndex: number) => string
  /** Whether to print each column's data type next to its name (lake previews do). */
  showColumnTypes?: boolean
  emphasis?: TabularEmphasis
  /** A Parquet row index to scroll into view when the rows arrive. */
  scrollToRow?: number | null

  // Column picking mode (for schema builder property mapping)
  columnPickingMode?: boolean
  onColumnPicked?: (columnName: string) => void
  // Cell picking mode (for row condition values)
  cellPickingMode?: boolean
  onCellPicked?: (columnName: string, cellValue: TabularCell) => void
  /** Picking context for contextual banner messages */
  pickingContext?: import('../lake/extraction-modal/types').PickingContext
}

export function TabularPreviewTable({
  columns,
  rows,
  currentOffset,
  totalRows,
  onPrevPage,
  onNextPage,
  isNavigating,
  headerRowIndex,
  pendingRows,
  pendingColumns,
  selectionMode,
  onRowClick,
  onColumnClick,
  onDragSelect,
  className = 'flex-1 flex flex-col overflow-hidden',
  rowLabel,
  showColumnTypes = true,
  emphasis,
  scrollToRow = null,
  columnPickingMode = false,
  onColumnPicked,
  cellPickingMode = false,
  onCellPicked,
  pickingContext,
}: TabularPreviewTableProps) {
  const rowIndexColIdx = columns.findIndex(c => c.name === ROW_INDEX_COLUMN)
  const dataCols = columns.filter(c => c.name !== ROW_INDEX_COLUMN)

  const rowsRef = useRef(rows)
  const columnsRef = useRef(columns)
  const onDragSelectRef = useRef(onDragSelect)
  rowsRef.current = rows
  columnsRef.current = columns
  onDragSelectRef.current = onDragSelect

  const dragRef = useRef<{
    active: boolean
    startRowPos: number // index into rows[] — handles non-contiguous __row_index (filtered data)
    startColIdx: number // 0-based data col index
    hasMoved: boolean // true once the pointer enters a cell other than the start cell
  } | null>(null)
  const dragPreviewRef = useRef<DragPreview | null>(null)
  const [dragPreview, setDragPreview] = useState<DragPreview | null>(null)
  const dragJustCompletedRef = useRef(false)
  const rowSelection = selectionMode === 'select-rows'

  const updateDragPreview = useCallback((value: DragPreview | null) => {
    dragPreviewRef.current = value
    setDragPreview(value)
  }, [])

  useEffect(() => {
    function handleMouseUp() {
      const drag = dragRef.current
      if (!drag?.active) return
      if (drag.hasMoved && onDragSelectRef.current && dragPreviewRef.current) {
        const {
          rowRange: [r0, r1],
          colRange: [c0, c1],
        } = dragPreviewRef.current
        const cols = columnsRef.current
        const riColIdx = cols.findIndex(c => c.name === ROW_INDEX_COLUMN)
        const selectedRows = rowsRef.current
          .slice(r0, r1 + 1)
          .map(row => (riColIdx >= 0 ? Number(row[riColIdx]) : -1))
          .filter(ri => ri >= 0)
        const selectedCols = Array.from({ length: c1 - c0 + 1 }, (_, i) => c0 + i)
        onDragSelectRef.current(selectedRows, selectedCols)
        dragJustCompletedRef.current = true
      }
      dragRef.current = null
      updateDragPreview(null)
    }
    window.addEventListener('mouseup', handleMouseUp)
    return () => window.removeEventListener('mouseup', handleMouseUp)
  }, [updateDragPreview])

  const scrollTarget = useRef<HTMLTableRowElement | null>(null)
  const scrolledTo = useRef<number | null>(null)
  const rowCount = rows.length
  useEffect(() => {
    if (scrollToRow === null || scrolledTo.current === scrollToRow || rowCount === 0) return
    if (scrollTarget.current) {
      scrollTarget.current.scrollIntoView({ block: 'center' })
      scrolledTo.current = scrollToRow
    }
  }, [scrollToRow, rowCount])

  function handleCellMouseDown(rowPos: number, colIdx: number, e: { preventDefault(): void }) {
    e.preventDefault()
    const startCol = rowSelection ? 0 : colIdx
    dragRef.current = { active: true, startRowPos: rowPos, startColIdx: startCol, hasMoved: false }
    updateDragPreview({
      rowRange: [rowPos, rowPos],
      colRange: rowSelection ? [0, dataCols.length - 1] : [colIdx, colIdx],
    })
  }

  function handleCellMouseEnter(rowPos: number, colIdx: number) {
    const drag = dragRef.current
    if (!drag?.active) return
    drag.hasMoved = drag.hasMoved || rowPos !== drag.startRowPos || colIdx !== drag.startColIdx
    const r0 = Math.min(drag.startRowPos, rowPos)
    const r1 = Math.max(drag.startRowPos, rowPos)
    const c0 = rowSelection ? 0 : Math.min(drag.startColIdx, colIdx)
    const c1 = rowSelection ? dataCols.length - 1 : Math.max(drag.startColIdx, colIdx)
    updateDragPreview({ rowRange: [r0, r1], colRange: [c0, c1] })
  }

  function getRowIndex(row: TabularCell[]): number {
    return rowIndexColIdx >= 0 ? Number(row[rowIndexColIdx]) : -1
  }

  function getCell(row: TabularCell[], colIdx: number): TabularCell {
    const originalCol = columns[colIdx < rowIndexColIdx || rowIndexColIdx < 0 ? colIdx : colIdx + 1]
    if (!originalCol) return null
    return row[columns.indexOf(originalCol)]
  }

  function rowSelected(rowIdx: number): boolean {
    return pendingRows.has(rowIdx) && (rowSelection || pendingColumns.size > 0)
  }

  function getCellBg(
    rowIdx: number,
    colIdx: number,
    rowPos: number,
    colName: string,
    value: TabularCell
  ) {
    if (rowIdx === headerRowIndex) return 'bg-primary/20'
    if (dragPreview) {
      const [r0, r1] = dragPreview.rowRange
      const [c0, c1] = dragPreview.colRange
      if (rowPos >= r0 && rowPos <= r1 && colIdx >= c0 && colIdx <= c1) return 'bg-primary/20'
    }
    if (rowSelection && pendingRows.has(rowIdx)) return 'bg-primary/10'
    if (pendingRows.has(rowIdx) && pendingColumns.has(colIdx))
      return 'bg-primary/10 ring-1 ring-inset ring-primary'
    if (emphasis?.cell?.(rowIdx, colName, value)) return 'bg-primary/15 font-medium text-primary'
    if (emphasis?.rows.has(rowIdx)) return 'bg-primary/10'
    return ''
  }

  function getRowNumberBg(rowIdx: number): string {
    if (rowIdx === headerRowIndex) return 'bg-primary/30 font-semibold'
    if (rowSelected(rowIdx)) return 'bg-primary/20 font-medium text-foreground'
    if (emphasis?.rows.has(rowIdx)) return 'bg-primary/10'
    return 'bg-muted/50'
  }

  function getColHeaderBg(colIdx: number): string {
    if (dragPreview && !rowSelection) {
      const [c0, c1] = dragPreview.colRange
      if (colIdx >= c0 && colIdx <= c1) return 'bg-primary/40 ring-1 ring-inset ring-primary'
    }
    if (pendingColumns.has(colIdx)) return 'bg-primary/30 ring-1 ring-inset ring-primary'
    return 'bg-background'
  }

  const isRowClickable = selectionMode !== null
  const isColClickable = selectionMode === 'build-batch'
  const isDragEnabled = (selectionMode === 'build-batch' || rowSelection) && !!onDragSelect

  const hasPrev = currentOffset > 0
  const hasNext = currentOffset + rows.length < totalRows
  const rangeStart = totalRows === 0 ? 0 : currentOffset + 1
  const rangeEnd = currentOffset + rows.length
  const hasPagination = hasPrev || hasNext || totalRows > 0

  if (columns.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
        No data to display
      </div>
    )
  }

  return (
    <div className={className}>
      {(columnPickingMode || cellPickingMode) && (
        <div className="bg-primary/10 border-l-4 border-primary px-4 py-2 mb-2 mx-3">
          <p className="text-sm font-medium">
            {cellPickingMode
              ? '👆 Click a cell below to use its value in Row Condition'
              : pickingContext?.type === 'property'
                ? '👆 Click a column header below to map the property'
                : '👆 Click a column header to use in Row Condition clause'}
          </p>
          <p className="text-xs text-muted-foreground">Press ESC to cancel</p>
        </div>
      )}

      <div className={cn('flex-1 overflow-auto px-3', dragPreview && 'select-none')}>
        <table className="text-xs border-collapse w-full min-w-max">
          <thead className="sticky top-0 z-10">
            <tr>
              <th className="bg-background border border-border px-2 py-1 text-left text-muted-foreground font-normal w-12 min-w-12">
                #
              </th>
              {dataCols.map((col, colIdx) => (
                <th
                  key={col.name}
                  className={cn(
                    'border border-border px-2 py-1 text-left font-medium whitespace-nowrap',
                    getColHeaderBg(colIdx),
                    columnPickingMode
                      ? 'cursor-pointer hover:bg-primary/30 ring-2 ring-primary/50 animate-pulse'
                      : isColClickable
                        ? 'cursor-pointer hover:bg-primary/25 select-none'
                        : ''
                  )}
                  onClick={() => {
                    if (columnPickingMode && onColumnPicked) onColumnPicked(col.name)
                    else if (isColClickable) onColumnClick(colIdx)
                  }}
                  title={
                    columnPickingMode
                      ? `✓ Select "${col.name}"`
                      : isColClickable
                        ? `Click to toggle column "${col.name}"`
                        : col.name
                  }
                >
                  <div className="flex items-center gap-1">
                    <span>{col.name}</span>
                    {showColumnTypes && (
                      <span className="text-muted-foreground font-normal text-[10px]">
                        ({col.data_type})
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowPos) => {
              const rowIdx = getRowIndex(row)
              return (
                <tr
                  key={rowIdx >= 0 ? rowIdx : rowPos}
                  ref={scrollToRow !== null && rowIdx === scrollToRow ? scrollTarget : undefined}
                  className={cn(isRowClickable && 'cursor-pointer hover:brightness-95')}
                  onClick={
                    isRowClickable
                      ? () => {
                          if (dragJustCompletedRef.current) {
                            dragJustCompletedRef.current = false
                            return
                          }
                          onRowClick(rowIdx)
                        }
                      : undefined
                  }
                >
                  <td
                    className={cn(
                      'border border-border px-2 py-0.5 text-right text-muted-foreground w-12 min-w-12 tabular-nums',
                      getRowNumberBg(rowIdx)
                    )}
                    title={`Row ${rowIdx}`}
                  >
                    {rowLabel ? rowLabel(rowIdx) : rowIdx}
                  </td>
                  {dataCols.map((col, colIdx) => {
                    const cellValue = getCell(row, colIdx)
                    return (
                      <td
                        key={col.name}
                        className={cn(
                          'border border-border px-2 py-0.5 max-w-xs truncate',
                          getCellBg(rowIdx, colIdx, rowPos, col.name, cellValue),
                          isDragEnabled && 'cursor-crosshair',
                          cellPickingMode &&
                            'cursor-pointer hover:bg-primary/20 ring-1 ring-primary/30'
                        )}
                        title={cellValue != null ? String(cellValue) : '(null)'}
                        onMouseDown={
                          isDragEnabled ? e => handleCellMouseDown(rowPos, colIdx, e) : undefined
                        }
                        onMouseEnter={
                          isDragEnabled ? () => handleCellMouseEnter(rowPos, colIdx) : undefined
                        }
                        onClick={() => {
                          if (cellPickingMode && onCellPicked) onCellPicked(col.name, cellValue)
                        }}
                      >
                        {cellValue != null ? (
                          String(cellValue)
                        ) : (
                          <span className="text-muted-foreground italic">null</span>
                        )}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {hasPagination && (
        <div className="border-t px-3 py-2 flex items-center gap-3 bg-background">
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs gap-1 max-md:h-9 max-md:px-3"
            onClick={onPrevPage}
            disabled={!hasPrev || isNavigating}
          >
            {isNavigating && !hasNext ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <ChevronLeft className="h-3.5 w-3.5" />
            )}
            {/* Phone: the arrow alone, so the row range keeps one line. */}
            <span className="max-md:sr-only">Previous</span>
          </Button>
          <span className="flex-1 text-center text-xs text-muted-foreground">
            {totalRows === 0
              ? 'No rows'
              : `Rows ${rangeStart.toLocaleString()}–${rangeEnd.toLocaleString()} of ${totalRows.toLocaleString()}`}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs gap-1 max-md:h-9 max-md:px-3"
            onClick={onNextPage}
            disabled={!hasNext || isNavigating}
          >
            <span className="max-md:sr-only">Next</span>
            {isNavigating && hasNext ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
      )}
    </div>
  )
}
